/**
 * Device Token Manager
 * Manages device token registration with the server
 * Ensures token is registered after user profile is loaded
 */

import { PushNotificationService } from './PushNotificationService'
import { registerDeviceToken } from '@/services/api/notifications'
import { useUserStore } from '@/stores/user'
import { useLocaleStore } from '@/stores/locale'
import { logger } from '@/services/logger'
import { Preferences } from '@capacitor/preferences'
import type { DeviceTokenPayload } from '@/types/notifications'

const DEVICE_TOKEN_KEY = 'device_push_token'
const TOKEN_REGISTERED_KEY = 'token_registered_for_worker'

/**
 * Initialize device token registration
 * Call this after user profile is loaded
 * For web platform, registers without token so server knows not to send push notifications
 */
export async function initializeDeviceTokenRegistration(): Promise<void> {
    const userStore = useUserStore()
    const localeStore = useLocaleStore()

    if (!userStore.workerId) {
        logger.warn('[DeviceTokenManager] Cannot register token: workerId not available')
        return
    }

    // For web platform, register without token so server knows this is a web device
    if (!PushNotificationService.isSupported()) {
        logger.info('[DeviceTokenManager] Registering web platform device (no push token)')
        try {
            await registerTokenWithServer(
                userStore.workerId,
                undefined, // No token for web
                'web',
                localeStore.getCurrentLocale()
            )
            logger.info('[DeviceTokenManager] Web platform device registered successfully')
        } catch (error) {
            logger.error('[DeviceTokenManager] Failed to register web platform device:', error as Error)
        }
        return
    }

    try {
        // Check if we have a cached token that hasn't been registered yet
        const cachedToken = await getCachedToken()
        if (cachedToken) {
            logger.info('[DeviceTokenManager] Found cached token, registering with server')
            await registerTokenWithServer(
                userStore.workerId,
                cachedToken.token,
                cachedToken.platform,
                localeStore.currentLocale
            )
        }

        // Setup listener for new tokens
        console.log('[DeviceTokenManager] Setting up token received listener...')
        PushNotificationService.onTokenReceived(async (deviceToken) => {
            console.log('[DeviceTokenManager] ========== TOKEN RECEIVED CALLBACK ==========')
            console.log('[DeviceTokenManager] Token preview:', deviceToken.token.substring(0, 20) + '...')
            console.log('[DeviceTokenManager] Platform:', deviceToken.platform)
            console.log('[DeviceTokenManager] Worker ID:', userStore.workerId || 'NOT SET')

            logger.info('[DeviceTokenManager] New device token received')

            // Cache the token
            console.log('[DeviceTokenManager] Caching token...')
            await cacheToken(deviceToken.token, deviceToken.platform)
            console.log('[DeviceTokenManager] ✅ Token cached')

            // Register with server if we have workerId
            if (userStore.workerId) {
                console.log('[DeviceTokenManager] Registering token with server...')
                await registerTokenWithServer(
                    userStore.workerId,
                    deviceToken.token,
                    deviceToken.platform,
                    localeStore.currentLocale
                )
                console.log('[DeviceTokenManager] ✅ Token registered with server')
            } else {
                logger.warn('[DeviceTokenManager] Token received but workerId not available yet')
                console.warn('[DeviceTokenManager] ⚠️ Cannot register token - no workerId available')
            }
            console.log('[DeviceTokenManager] =========================================')
        })
        console.log('[DeviceTokenManager] ✅ Token received listener registered')

        logger.info('[DeviceTokenManager] Device token registration initialized')
    } catch (error) {
        logger.error('[DeviceTokenManager] Failed to initialize:', error as Error)
    }
}

/**
 * Register token with server
 * For web platform, token is undefined
 */
async function registerTokenWithServer(
    workerId: number,
    token: string | undefined,
    platform: 'ios' | 'android' | 'web',
    locale: string
): Promise<void> {
    try {
        const payload: DeviceTokenPayload = {
            workerId,
            platform,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            locale
        }

        // Only include token if it exists (not for web platform)
        if (token) {
            payload.token = token
        }

        await registerDeviceToken(payload)

        // Mark as registered for this worker
        await Preferences.set({
            key: `${TOKEN_REGISTERED_KEY}_${workerId}`,
            value: token || 'web_no_token'
        })

        logger.info('[DeviceTokenManager] Device registered with server', {
            workerId,
            platform,
            locale,
            hasToken: !!token
        })
    } catch (error) {
        logger.error('[DeviceTokenManager] Failed to register device with server:', error as Error)
        throw error
    }
}

/**
 * Cache device token locally
 */
async function cacheToken(token: string, platform: 'ios' | 'android' | 'web'): Promise<void> {
    try {
        await Preferences.set({
            key: DEVICE_TOKEN_KEY,
            value: JSON.stringify({ token, platform })
        })
    } catch (error) {
        logger.error('[DeviceTokenManager] Failed to cache token:', error as Error)
    }
}

/**
 * Get cached device token
 */
async function getCachedToken(): Promise<{ token: string; platform: 'ios' | 'android' | 'web' } | null> {
    try {
        const { value } = await Preferences.get({ key: DEVICE_TOKEN_KEY })
        if (value) {
            return JSON.parse(value)
        }
        return null
    } catch (error) {
        logger.error('[DeviceTokenManager] Failed to get cached token:', error as Error)
        return null
    }
}

/**
 * Check if token needs to be re-registered for current user
 */
export async function shouldRegisterToken(workerId: number): Promise<boolean> {
    try {
        const cachedToken = await getCachedToken()
        if (!cachedToken) {
            return false
        }

        const { value } = await Preferences.get({
            key: `${TOKEN_REGISTERED_KEY}_${workerId}`
        })

        // Need to register if not registered for this worker or if token changed
        return !value || value !== cachedToken.token
    } catch (error) {
        logger.error('[DeviceTokenManager] Failed to check registration status:', error as Error)
        return false
    }
}

/**
 * Force re-registration of device token
 * Useful when locale changes
 * For web platform, re-registers without token
 */
export async function reregisterDeviceToken(): Promise<void> {
    const userStore = useUserStore()
    const localeStore = useLocaleStore()

    if (!userStore.workerId) {
        logger.warn('[DeviceTokenManager] Cannot re-register: workerId not available')
        return
    }

    // For web platform, register without token
    if (!PushNotificationService.isSupported()) {
        try {
            await registerTokenWithServer(
                userStore.workerId,
                undefined, // No token for web
                'web',
                localeStore.getCurrentLocale()
            )
            logger.info('[DeviceTokenManager] Web platform device re-registered with new locale')
        } catch (error) {
            logger.error('[DeviceTokenManager] Failed to re-register web platform device:', error as Error)
        }
        return
    }

    const cachedToken = await getCachedToken()
    if (!cachedToken) {
        logger.warn('[DeviceTokenManager] Cannot re-register: no cached token')
        return
    }

    try {
        await registerTokenWithServer(
            userStore.workerId,
            cachedToken.token,
            cachedToken.platform,
            localeStore.currentLocale
        )
        logger.info('[DeviceTokenManager] Token re-registered successfully')
    } catch (error) {
        logger.error('[DeviceTokenManager] Failed to re-register token:', error as Error)
    }
}

/**
 * Clear device token registration
 * Call this on logout
 */
export async function clearDeviceTokenRegistration(): Promise<void> {
    try {
        await Preferences.remove({ key: DEVICE_TOKEN_KEY })
        
        // Clear all registration markers
        const keys = await Preferences.keys()
        for (const key of keys.keys) {
            if (key.startsWith(TOKEN_REGISTERED_KEY)) {
                await Preferences.remove({ key })
            }
        }

        logger.info('[DeviceTokenManager] Device token registration cleared')
    } catch (error) {
        logger.error('[DeviceTokenManager] Failed to clear registration:', error as Error)
    }
}

